import type { RepeatingListColumn, RepeatingListRowState } from './assessmentQuestion';

/**
 * One question's pending values and its own error copy, as the screen holds
 * them between renders. Every field starts at the "nothing typed/selected yet"
 * value below, so a question the member has never touched needs no state slot
 * of its own — see `useAssessmentPendingState`.
 */
export type QuestionFieldValues = {
  selected: string[];
  dateValue: string;
  textValue: string;
  otherInput: string;
  dateError: string | null;
  numberError: string | null;
  phoneError: string | null;
  /** The question-level "please answer this" banner. */
  showRequiredError: boolean;
};

export const EMPTY_QUESTION_FIELD_VALUES: QuestionFieldValues = {
  selected: [],
  dateValue: '',
  textValue: '',
  otherInput: '',
  dateError: null,
  numberError: null,
  phoneError: null,
  showRequiredError: false,
};

/**
 * Everything `QuestionField` renders from: the values/errors above plus the
 * repeating-list rows, which are held in their own sub-store (they need a
 * synchronously-current mirror the other fields don't — see
 * `useRepeatingListRows`).
 */
export type QuestionFieldState = QuestionFieldValues & {
  rows: RepeatingListRowState[];
  /** The web half of announcing a row add/remove — an ARIA live region's text. */
  statusMessage: string;
};

/**
 * Everything `QuestionField` reports back. One object per question rather than
 * a dozen individual props, so the wiring lives in exactly one place
 * (`useAssessmentPendingState().handlers`) instead of being repeated by every
 * layout that renders a question.
 */
export type QuestionFieldHandlers = {
  onToggleChoice: (value: string) => void;
  onOtherInputChange: (text: string) => void;
  onDateChange: (text: string) => void;
  onDateBlur: () => void;
  onTextChange: (text: string) => void;
  onNumberBlur: () => void;
  onPhoneBlur: () => void;
  onUpdateRowValue: (rowId: string, columnName: string, text: string) => void;
  onValidateRowCell: (rowId: string, column: RepeatingListColumn) => void;
  onRemoveRow: (rowId: string) => void;
  /**
   * Renders an inline "+ Add another" control right under the rows. Absent in
   * single-question mode, where the sticky footer owns that control instead
   * (shared with Continue); present in grouped (`singleScreen`) mode, where
   * the footer holds only the page's single Continue and every repeatingList
   * question needs its own add-row affordance in place.
   */
  onAddRowInline?: () => void;
};
